import React, { useState } from "react";
import { reviseSection, approveRevision, deleteRevision } from "./api.js"; 
import { isProjectUnder90Days } from "./utils.js";
import { table_headers } from "./tableHeaders.js"
import "./ReviseFabTool.css";

const ReviseFabTool = ({ project_idx, section_name, isEditing, setIsEditing, title, originalContent, onApply }) => {
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [showReviseBubble, setShowReviseBubble] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [isNotResponseValid, setIsNotResponseValid] = useState(false);


  const handleFabClick = async () => {
    const isEligible = await isProjectUnder90Days(project_idx);
    if (!isEligible) {
      alert("Bu proje 90 günden eski. Yapay Zeka revizyonu artık kullanılamaz. Eğer Yapay Zeka revizyon özelliğini uzatmaya ihtiyacınız varsa lütfen bizimle iletişime geçin: info@ideatrum.com");
      return;
    }

    setShowReviseBubble((prev) => {
      const newState = !prev;
      if (newState) {
        setAiPrompt("");
        setAiResponse("");
      }
      return newState;
    });
  };

  const handleSubmitPrompt = async () => {
    if (!aiPrompt.trim()) return;
    setIsLoadingAI(true);
    setAiResponse("");
  
    try {
      const revisedText = await reviseSection(project_idx, section_name, aiPrompt);
      setAiResponse(revisedText);
    } catch (error) {
      let userMessage = 'Bir hata oluştu. Lütfen tekrar deneyin.';
      setIsNotResponseValid(true);

      if (error.response && error.response.data) {
        const serverResponse = error.response.data;
        
        // Check if the server response has a 'detail' property
        if (serverResponse.detail && typeof serverResponse.detail === 'string') {
          const parts = serverResponse.detail.split(': ');
          
          // If the split was successful, use the extracted part. Otherwise, use the full detail message.
          const extractedMessage = parts.length > 1 ? parts[1] : serverResponse.detail;
          userMessage = extractedMessage;
        } else if (typeof serverResponse === 'string') {
          // If the response is a simple string, use that.
          userMessage = serverResponse;
        }
      }

      console.error("Request failed:", error);
      setAiResponse(`Hata: ${userMessage}`);
    } finally {
      setIsLoadingAI(false);
    }
  };

  if (!isEditing) return null;

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        className="revise-fab"
        onClick={handleFabClick}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        🧠 AI
      </button>

      {showTooltip && <div className="revise-fab-tooltip">YAPAY ZEKA İLE REVİZE ET</div>}

      {showReviseBubble && (
        <div className="revise-bubble">
          {!aiResponse ? (
            <>
              <textarea
                placeholder={`"${title}" bölümü için IDEATRUM'dan ne yapmasını istediğinizi yazın...`}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
              />
              <button
                className="revise-submit-btn"
                onClick={handleSubmitPrompt}
                disabled={isLoadingAI}
              >
                {isLoadingAI ? "Revize ediliyor..." : "Gönder"}
              </button>
            </>
          ) : (
            <>
              <div className="revise-message">
                {(() => {
                  try {
                    const parsed = typeof aiResponse === "string" ? JSON.parse(aiResponse) : aiResponse;

                    if (typeof parsed === "string") {
                      return <div>{parsed}</div>;
                    }

                    if (Array.isArray(parsed)) {
                      
                      approveRevision(project_idx, section_name);
                      if (parsed.every(item => typeof item === "string")) {
                        return (
                          <ol>
                            {parsed.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ol>
                        );
                      }

                      if (parsed.every(item => typeof item === "object" && !Array.isArray(item))) {
                        if (section_name === "PRODUCTS") {
                          const allProductNames = [
                            ...new Set(
                              parsed.flatMap((row) =>
                                row.list_product_explanations?.map((item) => item.product_name) || []
                              )
                            )
                          ];

                          return (
                            <table className="ai-preview-table">
                              <thead>
                                <tr>
                                  <th>Teknik Özellik</th>
                                  {allProductNames.map((name, idx) => (
                                    <th key={idx}>{name === "project_outcome" ? "Proje Çıktısı" : name}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {parsed.map((row, rowIndex) => (
                                  <tr key={rowIndex}>
                                    <td>{row.feature_name}</td>
                                    {allProductNames.map((productName, colIndex) => {
                                      const match = row.list_product_explanations?.find(
                                        (item) => item.product_name === productName
                                      );
                                      return <td key={colIndex}>{match?.explanation || "—"}</td>;
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          );
                        }

                        // Default object table render (for other structured sections)
                        const columns = Object.keys(parsed[0] || {});
                        return (
                          <table className="ai-preview-table">
                            <thead>
                              <tr>
                                {columns.map((col) => (
                                  <th key={col}>{table_headers[col]}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {parsed.map((row, idx) => (
                                <tr key={idx}>
                                  {columns.map((col) => (
                                    <td key={col}>{row[col]}</td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        );
                      }

                    }

                    // Unrecognized structured object → JSON.stringify fallback
                    return <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(parsed, null, 2)}</pre>;

                  } catch (e) {
                    // Not JSON: just display the raw string
                    return <div>{aiResponse}</div>;
                  }
                })()}
                <div className="char-count">{`Karakter sayısı: ${aiResponse.length}`}</div>
              </div>

              <div className="revise-btn-group">
                <button
                    onClick={async () => {
                    try {
                        //await approveRevision(project_idx, section_name);
                        onApply(aiResponse);
                        setShowReviseBubble(false);
                        // setIsEditing(false);
                    } catch (error) {
                        console.error("Approval failed:", error);
                        // Optionally show an error to the user
                    }
                    }}
                  disabled={isNotResponseValid}
                >
                    KULLAN
                </button>
                <button
                  onClick={async () => {
                    try {
                      // Only delete if response is valid JSON or an expected revision format
                      const isStructured =
                        typeof aiResponse === "string"
                          ? aiResponse.trim().startsWith("{") || aiResponse.trim().startsWith("[")
                          : typeof aiResponse === "object";

                      if (isStructured) {
                        await deleteRevision(project_idx, section_name);
                      }

                      setShowReviseBubble(false);
                      setIsNotResponseValid(false);
                    } catch (error) {
                      console.error("Delete failed:", error);
                      setShowReviseBubble(false); // Still close on error
                    }
                  }}
                >
                  VAZGEÇ
                </button>
                </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviseFabTool;
